import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FileStorageServiceInterface } from '../../interfaces/file-storage.interface';
import { LZStringService } from '../l-z-string/l-z-string.service';
import { UpgradableEncryption } from '@evorait/pace-encryption';
import { isDefined } from '../../utilities/is-defined';
import { isErrorWithCode } from '../../utilities/is-error-with-code';

@Injectable()
export abstract class BaseStorageService implements FileStorageServiceInterface, UpgradableEncryption {
    protected constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        protected readonly LZString: LZStringService,
    ) {}

    public checkCompressedData(storageId: string): Observable<boolean> {
        return this.checkFile$(storageId);
    }

    /**
     * This is a wrapper function used to convert the value returned by checkFile() function from Promise to observable.
     *
     * @param fileName
     */
    private checkFile$(fileName: string): Observable<boolean> {
        return from(this.checkFile(fileName)).pipe(
            catchError((error) => {
                // if the file was not cached and we get an NOT_FOUND_ERR, then return 'false' instead of passing
                // the error on
                const suppressError = isErrorWithCode(error) && error.code === 1;

                if (!suppressError) {
                    this.log.error(
                        `BaseStorageService: checkFile$: Error when checking for file '${fileName}': ${JSON.stringify(
                            error,
                        )}`,
                    );
                    return throwError(error);
                }
                return of(false);
            }),
        );
    }

    protected abstract checkFile(fileName: string): Promise<boolean>;

    /**
     * Function that loads compressed data from indexedDB file store.
     *
     * @param storageId
     */
    public loadCompressedData<T>(storageId: string): Observable<T> {
        this.log.debug(`BaseStorageService: loadCompressedData: loading data from "${storageId}"`);
        return this.readAsArrayBuffer$(storageId).pipe(
            map((response) => {
                const compressedData = response.buffer;
                const uint8array = new Uint8Array(compressedData);
                const dataString = this.LZString.decompressFromUint8Array(uint8array);
                return JSON.parse(dataString) as T;
            }),
        );
    }

    /**
     * Function that loads compressed data from indexedDB file store.
     *
     * @param storageId
     */
    public loadCompressedDataWithTimestamp<T>(storageId: string): Observable<{ data: T; timestamp: number }> {
        this.log.debug(`BaseStorageService: loadCompressedData: loading data from "${storageId}"`);
        return this.readAsArrayBuffer$(storageId).pipe(
            map((response) => {
                const compressedData = response.buffer;
                const timestamp = response.timestamp;
                const uint8array = new Uint8Array(compressedData);
                const dataString = this.LZString.decompressFromUint8Array(uint8array);
                const data = JSON.parse(dataString) as T;
                return { data, timestamp };
            }),
        );
    }

    public loadCompressedDataWithTimestampFromLiveQuery<T>(
        storageId: string,
    ): Observable<{ data: T; timestamp: number } | undefined> {
        this.log.debug(`BaseStorageService: loadCompressedData: loading data from "${storageId}"`);
        return this.readAsArrayBufferFromLiveQuery$(storageId).pipe(
            map((response) => {
                if (!isDefined(response)) {
                    return undefined;
                }

                const compressedData = response.buffer;
                const timestamp = response.timestamp;
                const uint8array = new Uint8Array(compressedData);
                const dataString = this.LZString.decompressFromUint8Array(uint8array);
                const data = JSON.parse(dataString) as T;
                return { data, timestamp };
            }),
        );
    }

    /**
     * This is a wrapper function used to convert the value returned by readAsArrayBuffer() function from Promise to observable.
     *
     * @param fileName
     */
    private readAsArrayBuffer$(fileName: string): Observable<{ buffer: ArrayBuffer; timestamp: number }> {
        return from(this.readAsArrayBuffer(fileName)).pipe(
            catchError((error) => {
                const isFileNotFound = isErrorWithCode(error) && error.code === 1;
                if (isFileNotFound) {
                    this.log.warn(
                        `BaseStorageService: readAsArrayBuffer$: Tried reading '${fileName}', but it does not exist.`,
                    );
                } else {
                    this.log.error(
                        `BaseStorageService: readAsArrayBuffer$: Failed to read stored data from file '${fileName}'. Error Message: ${JSON.stringify(
                            error,
                        )}`,
                    );
                }
                return throwError(() => error);
            }),
        );
    }

    protected abstract readAsArrayBuffer(fileName: string): Promise<{ buffer: ArrayBuffer; timestamp: number }>;

    /**
     * This function is used to get data from IndexedDb via live query.
     */
    public abstract readAsArrayBufferFromLiveQuery$(
        fileName: string,
    ): Observable<{ buffer: ArrayBuffer; timestamp: number } | undefined>;

    public removeCompressedData(storageId: string): Observable<void> {
        return this.removeFile$(storageId);
    }

    /**
     * This is a wrapper function used to convert the value returned by removeFile() function from Promise to observable.
     *
     * @param fileName
     */
    private removeFile$(fileName: string): Observable<void> {
        return from(this.removeFile(fileName)).pipe(
            catchError((error) => {
                return throwError(error);
            }),
        );
    }

    protected abstract removeFile(fileName: string): Promise<void>;

    public storeCompressedData<T>(storageId: string, data: T, timestamp: number = Date.now()): Observable<T> {
        this.log.debug(`BaseStorageService: storeCompressedData: writing data to "${storageId}"`);
        return this.writeFile$(storageId, data, timestamp);
    }

    /**
     * This is a wrapper function used to convert the value returned by writeFile() function from Promise to observable.
     *
     * @param fileName
     * @param data
     * @param timestamp
     */
    private writeFile$<T>(fileName: string, data: T, timestamp: number = Date.now()): Observable<T> {
        const json = JSON.stringify(data);
        const compressed = this.LZString.compressToUint8Array(json);

        return from(this.writeFile(fileName, compressed, timestamp)).pipe(
            catchError((error) => {
                this.log.error(
                    `BaseStorageService: writeFile$: Failed saving data to file '${fileName}'. Error Message: ${JSON.stringify(
                        error,
                    )}`,
                );
                return throwError(error);
            }),
            map(() => data),
        );
    }

    protected abstract writeFile(fileName: string, content: Uint8Array, timestamp: number): Promise<void>;

    /**
     * Upgrades all entries from legacy encryption to the current encryption format.
     *
     * This method performs a read-write transaction on the collection of stored entries.
     * It finds all entries whose content starts with the legacy prefix,
     * re-encrypts their content using the `encryptionMigrator`, and updates the entries
     * in the database.
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns {Observable<boolean>} An observable that emits `true` once all legacy
     * encrypted entries have been successfully migrated.
     */
    public abstract upgradeEncryption(): Observable<boolean>;
    /**
     *
     * Checks if there are any entries using legacy encryption format.
     *
     * This method queries the collection of stored entries and determines whether
     * any entry's content starts with the legacy encryption prefix.
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns {Observable<boolean>} An observable that emits `true` if at least one
     * entry uses legacy encryption, otherwise `false`.
     */
    public abstract hasLegacyEncryption(): Observable<boolean>;
}
